import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { Score, SCORE_SIZE } from 'src/components/Score';
import { TextLink } from 'src/components/TextLink';
import { isNonEnglish, LANGUAGE } from 'src/data/generation';
import { generationToolAllowsScoresForNonEnglish, GENERATION_TOOL } from 'src/data/generationTool';
import { GenerationToolIcon } from 'src/components/ScoreGenerationTool';
import { calculateBlockedReason } from 'src/data/utils';
import { BlockedScoreTooltip } from 'src/components/BlockedScoreTooltip';
import { useStyles } from './useStyles';

export const DocumentScorePanel = ({
  className,
  instanceId,
  score,
  generationTool,
  onDetailsClick,
  onHover,
  expanded,
  loading,
  text,
  language,
  disabled,
  isLastBlockScore,
}) => {
  const styles = useStyles();
  const [hovered, setHovered] = useState(false);
  const localExpanded = expanded || hovered;
  const isDisableDetailsPopup =
    language && isNonEnglish(language) && generationToolAllowsScoresForNonEnglish(generationTool);

  const handleScoreMouseEnter = useCallback(() => {
    setHovered(true);
    onHover(instanceId);
  }, [onHover, instanceId]);

  const handleRootMouseLeave = useCallback(() => {
    setHovered(false);
    onHover(null);
  }, [onHover]);

  const handleDetailsClick = useCallback(() => {
    onDetailsClick(instanceId);
  }, [onDetailsClick, instanceId]);

  const blockedReason = useMemo(
    () => calculateBlockedReason({ language, text, generationTool }),
    [language, text, generationTool]
  );

  const generationToolIcon = generationTool && (
    <GenerationToolIcon
      className={styles.icon}
      tool={generationTool}
    />
  );

  return (
    <div
      className={cx(styles.root, className, {
        [styles.expanded]: localExpanded,
        [styles.disabled]: disabled,
      })}
      onMouseLeave={handleRootMouseLeave}
      onClick={disabled || isDisableDetailsPopup ? null : handleDetailsClick}
    >
      <BlockedScoreTooltip blockedReason={blockedReason}>
        <Score
          className={styles.score}
          score={blockedReason ? null : score}
          size={SCORE_SIZE.large}
          loading={loading}
          onMouseEnter={handleScoreMouseEnter}
          isTargetForHint={isLastBlockScore}
          generationToolIcon={generationToolIcon}
        />
      </BlockedScoreTooltip>

      <div className={styles.panel}>
        <TextLink className={cx(styles.link, { [styles.hidden]: isDisableDetailsPopup })}>
          Insights <span className={styles.arrow}>&rsaquo;</span>
        </TextLink>
      </div>
    </div>
  );
};

DocumentScorePanel.propTypes = {
  className: PropTypes.string,
  instanceId: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
  onDetailsClick: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
  generationTool: PropTypes.oneOf(Object.values(GENERATION_TOOL)),
  expanded: PropTypes.bool,
  loading: PropTypes.bool,
  text: PropTypes.string,
  language: PropTypes.oneOf(Object.values(LANGUAGE)),
  disabled: PropTypes.bool,
  isLastBlockScore: PropTypes.bool,
};

DocumentScorePanel.defaultProps = {
  className: null,
  expanded: false,
  loading: false,
  text: null,
  generationTool: null,
  language: null,
  disabled: false,
  isLastBlockScore: false,
};
